import _ from 'lodash'
import type { Ref } from '@wix/materializer'
import { createRef } from '@wix/materializer'
import type { Component, SlotsDataItem } from '@wix/thunderbolt-becky-types'
import { pointers } from '@wix/thunderbolt-becky-types'
import { createExperimentRef, createRefInResolvedRefsNamespaces } from '../../../byRefUtils'
import {
	DELETED_REF_COMPS_ASPECT,
	GHOST_COMP_STRUCTURE_OVERRIDES,
	INVALIDATE_STRUCTURE,
	KEEP_GHOST_COMPONENTS_REF,
	RESOLVED_REF_IS_GHOST_COMPONENT_ASPECT,
	SITE_NAMESPACE,
	STRUCTURE_ASPECT,
} from '../../../constants'
import { createByRefsisConsumptionCompNode } from '../../../compNodeUtils'
import type { isGhostCompToViewItem } from './isGhostComponent'
import { REF_DELIMITER } from '../../../../utils'

const {
	data: {
		DATA_MAPS: { SLOTS },
	},
	components: {
		PROPERTY_TYPES: { SLOTS_QUERY },
	},
} = pointers

type StructureDeps = {
	templateComp: Ref<Component>
	ancestorRefCompDeleted: Ref<boolean>
	slotsHostOverrideDataItem: Ref<SlotsDataItem>
	slotsDataItem: Ref<SlotsDataItem>
	ghostComponent: Ref<isGhostCompToViewItem>
	keepGhostComponents: Ref<boolean>
	removeQueriesFromGhostComps: Ref<boolean>
	_invalidateStructureRef: Ref<boolean>
}

export const structure = createByRefsisConsumptionCompNode<StructureDeps, Component | undefined>({
	getDependencies: (templateCompId, { id }, ancestorsChain, scopedCreateRef) => {
		const slotsQuery = `${id}-${SLOTS_QUERY}`
		const ancestorRefCompId = ancestorsChain.split(REF_DELIMITER)[0]
		return {
			templateComp: scopedCreateRef<Component>(STRUCTURE_ASPECT, templateCompId),
			ancestorRefCompDeleted: createRef<boolean>([SITE_NAMESPACE, DELETED_REF_COMPS_ASPECT, ancestorRefCompId]),
			slotsHostOverrideDataItem: createRef<SlotsDataItem>([SITE_NAMESPACE, SLOTS, slotsQuery]),
			slotsDataItem: createRefInResolvedRefsNamespaces<SlotsDataItem>(SLOTS, slotsQuery),
			ghostComponent: createRefInResolvedRefsNamespaces<isGhostCompToViewItem>(
				RESOLVED_REF_IS_GHOST_COMPONENT_ASPECT,
				id
			),
			keepGhostComponents: KEEP_GHOST_COMPONENTS_REF,
			removeQueriesFromGhostComps: createExperimentRef('specs.thunderbolt.remove_queries_from_ghost_comps'),
			_invalidateStructureRef: createRefInResolvedRefsNamespaces<boolean>(INVALIDATE_STRUCTURE, id),
		}
	},
	toViewItem: (
		inflatedStructure,
		{
			templateComp,
			ancestorRefCompDeleted,
			slotsHostOverrideDataItem,
			slotsDataItem,
			ghostComponent,
			keepGhostComponents,
			removeQueriesFromGhostComps,
			_invalidateStructureRef,
		}
	) => {
		if (!templateComp || ancestorRefCompDeleted) {
			// if ancestor ref comp was deleted from structure, we want to clean up also it's children
			return
		}

		if (ghostComponent?.isGhostComp) {
			if (keepGhostComponents) {
				inflatedStructure = {
					...inflatedStructure,
					isGhost: true,
					isHiddenInAllVariants: ghostComponent.isHiddenInAllVariants || false,
				}
			} else {
				const structurePropsToKeep = removeQueriesFromGhostComps
					? { ..._.pick(inflatedStructure, ['id', 'type', 'parent', 'metaData']), layout: {} } // TODO remove layout once specs.thunderbolt.remove_queries_from_ghost_comps is merged
					: inflatedStructure
				return {
					...structurePropsToKeep,
					...GHOST_COMP_STRUCTURE_OVERRIDES,
				}
			}
		}

		const slots = slotsHostOverrideDataItem?.slots || slotsDataItem?.slots
		if (slots) {
			return {
				...inflatedStructure,
				slots,
			}
		}

		return inflatedStructure
	},
})
